<template>
    <InfiniteScroller #default="{ listPortion }" :list="listFiltered">
        <div v-if="isFetching" class="l-padded">
            <VSpinner size="medium" :line-fg-color="spinnerColor" :speed="1" />
        </div>

        <div
            v-else-if="!listFiltered.length"
            class="l-padded l-inline l-center t-subtle t-small"
        >
            <span>
                {{ $t('shared.noSearchResult') }}
            </span>
        </div>

        <template v-else>
            <div class="l-inline l-center-v l-gap-2 filter-actions">
                <i18n path="showOf">
                    <b>{{ listFiltered.length }}</b>
                    <b>{{ list.length }}</b>
                </i18n>

                <a v-if="filterTracker.length" @click="$emit('clearFilter')">
                    {{ $t('clearFilter') }}
                </a>
            </div>

            <table>
                <thead>
                    <tr>
                        <th>
                            {{ $t('createdBy') }}

                            <IconButton @click="handleSort('created_by')">
                                <SortArrowIcon
                                    :direction="
                                        sortBy === 'created_by'
                                            ? sortDirection
                                            : 0
                                    "
                                    width="10"
                                    height="10"
                                />
                            </IconButton>
                        </th>

                        <th>
                            {{ $t('description') }}

                            <IconButton @click="handleSort('description')">
                                <SortArrowIcon
                                    :direction="
                                        sortBy === 'description'
                                            ? sortDirection
                                            : 0
                                    "
                                    width="10"
                                    height="10"
                                />
                            </IconButton>
                        </th>

                        <th>
                            {{ $t('validFrom') }}

                            <IconButton @click="handleSort('valid_from')">
                                <SortArrowIcon
                                    :direction="
                                        sortBy === 'valid_from'
                                            ? sortDirection
                                            : 0
                                    "
                                    width="10"
                                    height="10"
                                />
                            </IconButton>
                        </th>

                        <th>
                            {{ $t('validTo') }}

                            <IconButton @click="handleSort('valid_to')">
                                <SortArrowIcon
                                    :direction="
                                        sortBy === 'valid_to'
                                            ? sortDirection
                                            : 0
                                    "
                                    width="10"
                                    height="10"
                                />
                            </IconButton>
                        </th>

                        <th>
                            {{ $t('assetCount', [$t('shared.assets')]) }}

                            <IconButton @click="handleSort('assetCount')">
                                <SortArrowIcon
                                    :direction="
                                        sortBy === 'assetCount'
                                            ? sortDirection
                                            : 0
                                    "
                                    width="10"
                                    height="10"
                                />
                            </IconButton>
                        </th>

                        <th>
                            {{ $t('actions') }}
                        </th>
                    </tr>
                </thead>

                <template v-for="item in listPortion">
                    <tr :key="item.id">
                        <td>
                            {{ item.created_by || '–' }}
                        </td>

                        <td>
                            {{ item.description || '–' }}
                        </td>

                        <td>
                            {{ item.fromTimestamp }}
                        </td>

                        <td>
                            {{ item.toTimestamp }}
                        </td>

                        <td>
                            {{ item.assetCount }}
                        </td>

                        <td>
                            <div class="action-group l-inline l-gap-1">
                                <div class="l-inline l-gap-1">
                                    <IconButton
                                        class="action-chip action-chip--neutral"
                                        :title="
                                            $t('editActionTitle', [
                                                item.description || item.id,
                                            ])
                                        "
                                        @click="handleEdit(item)"
                                    >
                                        <PencilIcon />
                                    </IconButton>

                                    <IconButton
                                        class="action-chip action-chip--static"
                                        :title="
                                            $t('copyLinkActionTitle', [
                                                item.description || item.id,
                                            ])
                                        "
                                        @click="handleCopyLink(item)"
                                    >
                                        <CopyIcon />
                                    </IconButton>
                                </div>

                                <div class="action-group-danger">
                                    <IconButton
                                        class="action-chip action-chip--danger"
                                        :title="
                                            $t('deleteActionTitle', [
                                                item.description || item.id,
                                            ])
                                        "
                                        @click="handleDelete(item)"
                                    >
                                        <TrashBinIcon />
                                    </IconButton>
                                </div>
                            </div>
                        </td>
                    </tr>
                </template>
            </table>
        </template>
    </InfiniteScroller>
</template>

<script>
import moment from 'moment-timezone'
import VSpinner from 'vue-simple-spinner'

import { httpHelper } from '@/utils'
import IconButton from '@/components/IconButton'
import InfiniteScroller from '@/components/InfiniteScroller'
import PencilIcon from '@/components/icons/PencilIcon'
import SharingTokenEditModal from '@/components/redesigned/SharingTokenEditModal'
import SortArrowIcon from '@/components/icons/SortArrowIcon'
import TrashBinIcon from '@/components/icons/TrashBinIcon'
import CopyIcon from '@/components/icons/CopyIcon'

const defaultSortColumn = 'valid_from'
const defaultSortDirection = -1

export default {
    name: 'SharingTokensTable',
    components: {
        IconButton,
        InfiniteScroller,
        PencilIcon,
        SortArrowIcon,
        TrashBinIcon,
        CopyIcon,
        VSpinner,
    },
    props: {
        filterTracker: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            isFetching: false,
            list: [],
            sortBy: defaultSortColumn,
            sortDirection: defaultSortDirection,
            spinnerColor: process.env.VUE_APP_COLOR_PRIMARY,
        }
    },
    computed: {
        listFiltered() {
            if (this.sortBy) {
                return this.list.slice().sort((a, b) => {
                    const aValue = a[this.sortBy]
                    const bValue = b[this.sortBy]

                    if (
                        this.sortBy === 'valid_from' ||
                        this.sortBy === 'valid_to'
                    ) {
                        return (
                            this.sortDirection *
                            (new Date(bValue).getTime() -
                                new Date(aValue).getTime())
                        )
                    } else if (aValue === bValue) {
                        return 0
                    } else if (aValue == null) {
                        return 1
                    } else if (bValue == null) {
                        return -1
                    } else if (typeof aValue === 'string') {
                        return aValue.localeCompare(bValue) * this.sortDirection
                    }

                    return aValue > bValue
                        ? this.sortDirection
                        : -this.sortDirection
                })
            }

            return this.list
        },
    },
    watch: {
        filterTracker() {
            this.fetchSharingTokens()
        },
    },
    mounted() {
        this.fetchSharingTokens()
    },
    methods: {
        async fetchSharingTokens() {
            this.isFetching = true
            const { data } = await httpHelper.getRecursively('/share/tokens/', {
                params: {
                    limit: process.env.VUE_APP_LIMIT_RECORDS_PER_REQUEST,
                    assets: this.filterTracker.map(tracker => tracker.asset),
                },
            })
            this.list = data.results.map(item => ({
                ...item,
                assetCount: item.assets.length,
                fromTimestamp: moment(item.valid_from).format(
                    'DD.MM.YYYY HH:mm'
                ),
                toTimestamp: moment(item.valid_to).format('DD.MM.YYYY HH:mm'),
            }))
            this.isFetching = false
        },
        async handleDelete(token) {
            this.isFetching = true
            await httpHelper.drop(`/share/tokens/${token.id}/`)
            this.fetchSharingTokens()
        },
        handleEdit(token) {
            this.$modal.show(
                SharingTokenEditModal,
                { token, onSubmit: this.fetchSharingTokens },
                { width: 400, height: '75%' }
            )
        },
        handleSort(column) {
            if (this.sortBy !== column || this.sortDirection !== 1) {
                this.sortBy = column
                this.sortDirection = 1
            } else if (this.sortDirection === 1) {
                this.sortDirection = -1
            }
        },
        async handleCopyLink(token) {
            const link = `${window.location.origin}/#/share/${token.token}`
            try {
                await navigator.clipboard.writeText(link)
                this.$notify(this.$t('actionCopyDone'))
            } catch {
                this.$notify({
                    title: this.$t('shared.error'),
                    text: this.$t('copyLinkError'),
                })
            }
        },
    },
}
</script>

<i18n>
{
    "en": {
        "actions": "Actions",
        "assetCount": "Number of {0}",
        "clearFilter": "Clear filter",
        "createdBy": "Created by",
        "delete": "Delete",
        "copyLink": "Copy Link",
        "copyLinkError": "Could not copy the link. Please try again.",
        "copyLinkActionTitle": "Copy link for token {0}",
        "deleteActionTitle": "Delete token {0}",
        "actionCopyDone": "Copied",
        "description": "Description",
        "edit": "Edit",
        "editActionTitle": "Edit token {0}",
        "showOf": "Show {0} of {1}",
        "validFrom": "Valid from",
        "validTo": "Valid to"
    },
    "de": {
        "actions": "Aktionen",
        "assetCount": "Anzahl {0}",
        "clearFilter": "Filter zurücksetzen",
        "createdBy": "Erstellt von",
        "delete": "Löschen",
        "copyLink": "Link kopieren",
        "copyLinkError": "Der Link konnte nicht kopiert werden. Bitte versuchen Sie es erneut.",
        "copyLinkActionTitle": "Link für Token {0} kopieren",
        "deleteActionTitle": "Token {0} löschen",
        "actionCopyDone": "Kopiert",
        "description": "Beschreibung",
        "edit": "Bearbeiten",
        "editActionTitle": "Token {0} bearbeiten",
        "showOf": "Zeige {0} von {1}",
        "validFrom": "Gültig ab",
        "validTo": "Gültig bis"
    },
    "fr": {
        "actions": "Actions",
        "assetCount": "Total {0}",
        "clearFilter": "Supprimer le filtre",
        "createdBy": "Créé par",
        "delete": "Supprimer",
        "copyLink": "Copier le lien",
        "copyLinkError": "Le lien n'a pas pu être copié. Veuillez réessayer.",
        "copyLinkActionTitle": "Copier le lien du token {0}",
        "deleteActionTitle": "Supprimer le token {0}",
        "actionCopyDone": "Copié",
        "description": "Description",
        "edit": "Editer",
        "editActionTitle": "Modifier le token {0}",
        "showOf": "Afficher {0} de {1}",
        "validFrom": "Valide à partir du",
        "validTo": "Valide jusqu’au"
    },
    "it": {
        "actions": "Azione",
        "assetCount": "Totale {0}",
        "clearFilter": "Annullare il filtro",
        "createdBy": "Creato da",
        "delete": "Cancellare",
        "copyLink": "Copia link",
        "copyLinkError": "Impossibile copiare il link. Riprova.",
        "copyLinkActionTitle": "Copia il link del token {0}",
        "deleteActionTitle": "Elimina token {0}",
        "actionCopyDone": "Copiato",
        "description": "Descrizione",
        "edit": "Condizione",
        "editActionTitle": "Modifica token {0}",
        "showOf": "Listo {0} di {1}",
        "validFrom": "Valido dal",
        "validTo": "Valido fino al"
    }
}
</i18n>

<style lang="scss" scoped>
.filter-actions {
    position: sticky;
    top: 0;
    left: 0;
    padding: 1rem 2rem;
    height: 52px;
    border-bottom: $style-border;
    background-color: #fff;
    font-size: 16px;
    color: rgba(0, 0, 0, 0.7);
    z-index: 10;

    a {
        font-size: 13px;
        color: $color-text-new;
        text-decoration: underline;
        cursor: pointer;

        &:hover {
            text-decoration: none;
        }
    }
}

table {
    display: block;
    width: 100%;
    border-spacing: 0;
    font-size: 15px;
    color: rgba(0, 0, 0, 0.7);

    thead {
        position: sticky;
        top: 52px;
        background-color: #fff;
        z-index: 10;

        .icon-button {
            margin-left: 5px;
        }
    }

    tr {
        &:not(:first-of-type) {
            td {
                border-top: $style-border;
            }
        }

        th {
            text-align: left;
            border-bottom: $style-border;
        }

        th,
        td {
            padding: 1rem;
            white-space: nowrap;

            &:first-child {
                padding-left: 2rem;
            }

            &:last-child {
                padding-right: 2rem;
            }

            &:not(:last-child) {
                width: 1%;
            }
        }
    }

    a {
        display: block;

        .icon-button {
            display: block;
        }
    }
}
</style>
